// src/pages/NewBooksPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Spinner, Alert, Card } from 'react-bootstrap';
import { FaSearch, FaBook, FaHome, FaSignOutAlt, FaPlus, FaBookmark, 
         FaCheckCircle, FaBookOpen, FaCompass, FaListUl, FaUser } from 'react-icons/fa';
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase/firebase-config";
import { collection, addDoc, getDocs, query, where, limit } from "firebase/firestore";
import '../styles/Dashboard.css';

// Updated to use Google Books API directly since your custom backend has CORS issues
const GOOGLE_BOOKS_API_URL = 'https://www.googleapis.com/books/v1/volumes';

// Expanded list of popular subjects for suggested books
const POPULAR_SUBJECTS = [
  
  'technology', 
  'history', 
  'business', 
  'self-help',
  'psychology',
  'philosophy'
];

function NewBooksPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [bookResults, setBookResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('discover');
  const [addingBook, setAddingBook] = useState(null);
  const [searchType, setSearchType] = useState('general');
  const [currentAuthor, setCurrentAuthor] = useState('');
  const [suggestedBooks, setSuggestedBooks] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [suggestedBooksTitle, setSuggestedBooksTitle] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = localStorage.getItem("isAdmin") === "true";

  // Load suggested books when component mounts
  useEffect(() => {
    fetchSuggestedBooks();
  }, []);

  // Fetch random popular books as suggestions
  const fetchSuggestedBooks = async () => {
    setLoadingSuggestions(true);
    
    try {
      // Select a random subject from our list of popular subjects
      const randomSubject = POPULAR_SUBJECTS[Math.floor(Math.random() * POPULAR_SUBJECTS.length)];
      setSuggestedBooksTitle(`Suggested ${randomSubject.charAt(0).toUpperCase() + randomSubject.slice(1)} Books`);
      
      const response = await fetch(`${GOOGLE_BOOKS_API_URL}?q=subject:${randomSubject}&maxResults=6`);
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();
      
      if (data && Array.isArray(data.items)) {
        setSuggestedBooks(data.items.map(item => {
          const volumeInfo = item.volumeInfo || {};
          return {
            id: item.id,
            title: volumeInfo.title || 'Unknown Title',
            authors: volumeInfo.authors || ['Unknown Author'],
            description: volumeInfo.description || 'No description available',
            categories: [randomSubject, ...(volumeInfo.categories || [])],
            imageUrl: volumeInfo.imageLinks?.thumbnail || '',
            publishedDate: volumeInfo.publishedDate || '',
            pageCount: volumeInfo.pageCount || 0,
            averageRating: volumeInfo.averageRating || 0,
            infoLink: volumeInfo.infoLink || '',
            previewLink: volumeInfo.previewLink || ''
          };
        }));
      }
    } catch (error) {
      console.error("Error fetching suggested books:", error);
      // Fallback
      setSuggestedBooksTitle("Suggested Books For You");
      await fetchBooksBySubject('fiction');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Fetch books by subject using Google Books API
  const fetchBooksBySubject = async (subject) => {
    try {
      const response = await fetch(`${GOOGLE_BOOKS_API_URL}?q=subject:${subject}&maxResults=4`);
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();
      
      if (data && Array.isArray(data.items)) {
        setSuggestedBooks(data.items.map(item => {
          const volumeInfo = item.volumeInfo || {};
          return {
            id: item.id,
            title: volumeInfo.title || 'Unknown Title',
            authors: volumeInfo.authors || ['Unknown Author'],
            description: volumeInfo.description || 'No description available',
            categories: volumeInfo.categories || [],
            imageUrl: volumeInfo.imageLinks?.thumbnail || '',
            publishedDate: volumeInfo.publishedDate || '',
            pageCount: volumeInfo.pageCount || 0,
            averageRating: volumeInfo.averageRating || 0,
            infoLink: volumeInfo.infoLink || '',
            previewLink: volumeInfo.previewLink || ''
          };
        }));
      }
    } catch (error) {
      console.error("Error fetching books by subject:", error);
    }
  };

  // Modified handleSearch to set hasSearched to true
  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim() && searchType === 'general') return;

    setLoading(true);
    setError(null);
    setHasSearched(true); // Set to true whenever a search is performed

    try {
      // Build the search query based on searchType
      let apiQuery = searchType === 'author' 
        ? `inauthor:"${encodeURIComponent(currentAuthor)}"`
        : encodeURIComponent(searchQuery);
      
      // Using Google Books API directly with maxResults=20 parameter
      const response = await fetch(`${GOOGLE_BOOKS_API_URL}?q=${apiQuery}&maxResults=20`);
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();
      
      if (data && Array.isArray(data.items)) {
        setBookResults(data.items.map(item => {
          const volumeInfo = item.volumeInfo || {};
          return {
            id: item.id,
            title: volumeInfo.title || 'Unknown Title',
            authors: volumeInfo.authors || ['Unknown Author'],
            description: volumeInfo.description || 'No description available',
            categories: volumeInfo.categories || [],
            imageUrl: volumeInfo.imageLinks?.thumbnail || '',
            publishedDate: volumeInfo.publishedDate || '',
            pageCount: volumeInfo.pageCount || 0,
            averageRating: volumeInfo.averageRating || 0,
            infoLink: volumeInfo.infoLink || '',
            previewLink: volumeInfo.previewLink || ''
          };
        }));
      } else {
        setBookResults([]);
        setError('No books found. Try a different search term.');
      }
      
      // Clear the search input after fetching results (only for general searches)
      if (searchType === 'general') {
        setSearchQuery('');
      }
    } catch (err) {
      console.error('Error searching books:', err);
      setError('Error searching books. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Effect to check for URL parameters when the component mounts
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const authorParam = searchParams.get('author');
    
    if (authorParam) {
      // If an author parameter is present, set up for author search
      setSearchType('author');
      setCurrentAuthor(authorParam);
      
      // Show a message
      setError({ 
        type: 'info', 
        message: `Showing books by ${authorParam}...` 
      });
      
      // Trigger search after a brief delay to allow UI to update
      setTimeout(() => {
        // Create a direct search for this author
        (async () => {
          try {
            setLoading(true);
            const apiQuery = `inauthor:"${encodeURIComponent(authorParam)}"`;
            const response = await fetch(`${GOOGLE_BOOKS_API_URL}?q=${apiQuery}&maxResults=20`);
            
            if (!response.ok) {
              throw new Error(`API request failed with status ${response.status}`);
            }

            const data = await response.json();
            
            if (data && Array.isArray(data.items)) {
              setBookResults(data.items.map(item => {
                const volumeInfo = item.volumeInfo || {};
                return {
                  id: item.id,
                  title: volumeInfo.title || 'Unknown Title',
                  authors: volumeInfo.authors || ['Unknown Author'],
                  description: volumeInfo.description || 'No description available',
                  categories: volumeInfo.categories || [],
                  imageUrl: volumeInfo.imageLinks?.thumbnail || '',
                  publishedDate: volumeInfo.publishedDate || '',
                  pageCount: volumeInfo.pageCount || 0,
                  averageRating: volumeInfo.averageRating || 0,
                  infoLink: volumeInfo.infoLink || '',
                  previewLink: volumeInfo.previewLink || ''
                };
              }));
            } else {
              setBookResults([]);
              setError(`No books found by ${authorParam}. Try another author.`);
            }
          } catch (err) {
            console.error('Error searching for author:', err);
            setError(`Error searching for books by ${authorParam}. Please try again.`);
          } finally {
            setLoading(false);
          }
        })();
      }, 100);
    }
  }, [location.search]); // Remove handleSearch from dependency array

  // New function to handle author clicks - without useCallback
  const handleAuthorClick = (author) => {
    setSearchType('author');
    setCurrentAuthor(author);
    setBookResults([]); // Clear previous results
    setLoading(true); // Start loading immediately
    
    // Display a message that we're showing books by this author
    setError({ 
      type: 'info', 
      message: `Showing books by ${author}. Search results will update shortly...` 
    });
    
    // Update the URL to reflect the author search (without causing navigation)
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('author', author);
    const newRelativePathQuery = `${location.pathname}?${searchParams.toString()}`;
    window.history.pushState(null, '', newRelativePathQuery);
    
    // Direct API call
    (async () => {
      try {
        const apiQuery = `inauthor:"${encodeURIComponent(author)}"`;
        const response = await fetch(`${GOOGLE_BOOKS_API_URL}?q=${apiQuery}&maxResults=20`);
        
        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();
        
        if (data && Array.isArray(data.items)) {
          setBookResults(data.items.map(item => {
            const volumeInfo = item.volumeInfo || {};
            return {
              id: item.id,
              title: volumeInfo.title || 'Unknown Title',
              authors: volumeInfo.authors || ['Unknown Author'],
              description: volumeInfo.description || 'No description available',
              categories: volumeInfo.categories || [],
              imageUrl: volumeInfo.imageLinks?.thumbnail || '',
              publishedDate: volumeInfo.publishedDate || '',
              pageCount: volumeInfo.pageCount || 0,
              averageRating: volumeInfo.averageRating || 0,
              infoLink: volumeInfo.infoLink || '',
              previewLink: volumeInfo.previewLink || ''
            };
          }));
        } else {
          setBookResults([]);
          setError(`No books found by ${author}. Try another author.`);
        }
      } catch (err) {
        console.error('Error searching for author:', err);
        setError(`Error searching for books by ${author}. Please try again.`);
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("uid");
      localStorage.removeItem("isAdmin");
      navigate("/login");
    } catch (error) {
      console.error("Error signing out: ", error);
      setError("Failed to sign out. Please try again.");
    }
  };

  // Regular library adding function - includes navigation to dashboard
  const handleAddToLibrary = async (book, readingStatus = 'toread') => {
    setAddingBook(book.id);
    try {
      const uid = localStorage.getItem("uid");
      if (!uid) {
        throw new Error("User not authenticated");
      }
      
      await addDoc(collection(db, "books"), {
        title: book.title,
        author: book.authors.join(', '),
        description: book.description,
        imageUrl: book.imageUrl,
        userId: uid,
        readingStatus: readingStatus,
        rating: 0,
        tags: book.categories,
        dateAdded: new Date().toISOString(),
        googleBookId: book.id,
        publishedDate: book.publishedDate,
        pageCount: book.pageCount,
        websiteLink: book.infoLink
      });

      // Show success message and navigate to dashboard
      setError({ type: 'success', message: `"${book.title}" added to your library!` });
      
      // Navigate to dashboard after adding to library
      setTimeout(() => {
        navigate('/');
      }, 1500);
      
    } catch (err) {
      console.error("Error adding book:", err);
      setError({ type: 'danger', message: "Failed to add book. Please try again." });
    } finally {
      setAddingBook(null);
    }
  };
  
  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <FaBook className="me-2" /> Book Manager
          </div>
        </div>

        <div className="sidebar-nav">
          <div className="nav-item">
            <a 
              href="/" 
              className={`nav-link ${activeCategory === 'dashboard' ? 'active' : ''}`}
            >
              <FaHome className="nav-icon" /> Dashboard
            </a>
          </div>

          <div className="sidebar-divider"></div>
          
          <div className="sidebar-heading">Discover</div>
          
          <div className="nav-item">
            <a 
              href="#" 
              className={`nav-link active`}
              onClick={() => setActiveCategory('discover')}
            >
              <FaCompass className="nav-icon" /> Find New Books
            </a>
          </div>

          <div className="sidebar-divider"></div>
          
          <div className="sidebar-heading">My Books</div>
          
          <div className="nav-item">
            <a 
              href="/#completed" 
              className={`nav-link ${activeCategory === 'completed' ? 'active' : ''}`}
            >
              <FaCheckCircle className="nav-icon" /> Already Read
            </a>
          </div>
          
          <div className="nav-item">
            <a 
              href="/#reading" 
              className={`nav-link ${activeCategory === 'reading' ? 'active' : ''}`}
            >
              <FaBookOpen className="nav-icon" /> Reading Now
            </a>
          </div>
          
          <div className="nav-item">
            <a 
              href="/#toread" 
              className={`nav-link ${activeCategory === 'toread' ? 'active' : ''}`}
            >
              <FaBookmark className="nav-icon" /> To Be Read
            </a>
          </div>

          <div className="sidebar-divider"></div>
          
          <div className="sidebar-heading">Actions</div>
          
          {isAdmin && (
            <div className="nav-item">
              <a href="#" className="nav-link" onClick={() => navigate("/book/new")}>
                <FaPlus className="nav-icon" /> Add New Book
              </a>
            </div>
          )}
          
          <div className="nav-item">
            <a href="#" className="nav-link" onClick={handleSignOut}>
              <FaSignOutAlt className="nav-icon" /> Sign Out
            </a>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <Container fluid>
          {/* Header & Search Form */}
          <Row className="mb-4">
            <Col lg={12}>
              <h2 className="mb-4">
                {searchType === 'author' ? `Books by ${currentAuthor}` : 'Find New Books'}
                {searchType === 'author' && (
                  <Button 
                    variant="outline-secondary"
                    size="sm"
                    className="ms-3"
                    onClick={() => {
                      setSearchType('general');
                      setCurrentAuthor('');
                      setBookResults([]);
                      // Update the URL to remove author parameter
                      navigate('/discover');
                    }}
                  >
                    Return to general search
                  </Button>
                )}
              </h2>
              <Form onSubmit={handleSearch} className="mb-4">
                <Row className="align-items-center">
                  <Col lg={10} md={9} sm={8}>
                    <div className="search-container w-100">
                      <FaSearch className="search-icon" />
                      <Form.Control
                        type="text"
                        placeholder="Search by title, author, or keyword..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="mb-0"
                      />
                    </div>
                  </Col>
                  <Col lg={2} md={3} sm={4}>
                    <Button 
                      variant="primary" 
                      type="submit" 
                      className="w-100" 
                      disabled={loading || !searchQuery.trim()}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" animation="border" role="status" className="me-2" />
                          Searching...
                        </>
                      ) : 'Search'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>

          {/* Status Messages */}
          {error && (
            <Alert 
              variant={typeof error === 'object' ? error.type : 'danger'} 
              onClose={() => setError(null)} 
              dismissible
              className="mb-4"
            >
              {typeof error === 'object' ? error.message : error}
            </Alert>
          )}

          {/* Search Results Section */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" variant="primary" />
              <p className="mt-3">Searching for books...</p>
            </div>
          ) : (
            <>
              {bookResults.length > 0 && (
                <>
                  <h4 className="mb-4">Search Results ({bookResults.length})</h4>
                  <div className="book-results-container" style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    {bookResults.map((book) => (
                      <div key={book.id} className="mb-4 bg-white" style={{ 
                        borderRadius: '12px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        padding: '24px',
                        border: '1px solid #eaeaea'
                      }}>
                        <div className="d-flex">
                          {/* Book Cover */}
                          <div className="me-4" style={{ width: '120px', minWidth: '120px' }}>
                            {book.imageUrl ? (
                              <img 
                                src={book.imageUrl} 
                                alt={book.title} 
                                style={{ 
                                  width: '100%',
                                  height: '170px',
                                  objectFit: 'contain',
                                  borderRadius: '4px',
                                }}
                              />
                            ) : (
                              <div 
                                className="d-flex justify-content-center align-items-center bg-light"
                                style={{ 
                                  height: '170px', 
                                  borderRadius: '4px',
                                }}
                              >
                                <FaBook size={40} className="text-secondary" />
                              </div>
                            )}
                          </div>

                          {/* Book Details */}
                          <div style={{ flex: 1 }}>
                            <div className="d-flex justify-content-between">
                              {/* Title and Author */}
                              <div style={{ flex: 1, paddingRight: '20px' }}>
                                <h5 className="mb-1" style={{ fontWeight: '600' }}>{book.title}</h5>
                                <p className="text-muted mb-2">
                                  by{' '}
                                  {book.authors.map((author, index) => (
                                    <span key={index}>
                                      <span 
                                        onClick={() => handleAuthorClick(author)} 
                                        style={{ 
                                          cursor: 'pointer', 
                                          color: '#5271ff',
                                          fontWeight: '500',
                                          textDecoration: 'underline'
                                        }}
                                      >
                                        {author}
                                      </span>
                                      {index < book.authors.length - 1 ? ', ' : ''}
                                    </span>
                                  ))}
                                </p>
                                
                                {/* Description - Moved under the author */}
                                <p className="book-description" style={{ 
                                  fontSize: '0.9rem', 
                                  color: '#555',
                                  lineHeight: '1.5',
                                  marginBottom: 0
                                }}>
                                  {book.description && book.description.length > 300
                                    ? `${book.description.substring(0, 300)}...`
                                    : book.description || 'No description available'}
                                </p>
                              </div>

                              {/* Action Buttons - Aligned to the right */}
                              <div className="d-flex flex-column" style={{ 
                                minWidth: '180px', 
                                alignItems: 'stretch'
                              }}>
                                <Button
                                  variant="primary"
                                  className="mb-2 w-100"
                                  onClick={() => handleAddToLibrary(book, 'toread')}
                                  disabled={addingBook === book.id}
                                  style={{ 
                                    borderRadius: '8px',
                                    padding: '10px',
                                    fontWeight: '500',
                                    border: 'none'
                                  }}
                                >
                                  {addingBook === book.id ? (
                                    <>
                                      <Spinner size="sm" animation="border" role="status" className="me-2" />
                                      Adding...
                                    </>
                                  ) : (
                                    'Add to Library'
                                  )}
                                </Button>
                                
                                <Button
                                  variant="outline-info"
                                  as="a"
                                  href={book.infoLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-100 text-center"
                                  style={{ 
                                    borderRadius: '8px',
                                    padding: '10px',
                                    borderColor: '#1dc2db',
                                    color: '#1dc2db',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = '#1dc2db';
                                    e.currentTarget.style.color = '#fff';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = '';
                                    e.currentTarget.style.color = '#1dc2db';
                                  }}
                                >
                                  More Info
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {!loading && searchQuery && bookResults.length === 0 && !error && (
                <div className="empty-state py-5 my-4">
                  <div className="py-5">
                    <h4 className="text-muted mb-3">No books found</h4>
                    <p>Try searching with different keywords or check your spelling.</p>
                  </div>
                </div>
              )}
              {!searchQuery && !loading && !hasSearched && (
                <>
                  <div className="empty-state text-center py-5 my-4">
                    <div className="py-4">
                      <h4 className="text-muted mb-3">Discover your next favorite book</h4>
                      <p>Use the search bar above to find books by title, author, or keyword.</p>
                    </div>
                  </div>
                  
                  {/* Suggested Books Section - Centered layout */}
                  {suggestedBooks.length > 0 && (
                    <div className="suggested-books mt-5" style={{ maxWidth: '1200px', margin: '0 auto' }}>
                      <h5 className="mb-4 text-primary fw-bold position-relative pb-2" style={{
                        display: 'inline-block',
                        borderBottom: '2px solid #5271ff'
                      }}>
                        {suggestedBooksTitle}
                      </h5>
                      <Row className="g-4 justify-content-center">
                        {suggestedBooks.map((book) => (
                          <Col key={book.id} lg={2} md={3} sm={6} xs={6} className="d-flex justify-content-center">
                            <Card className="h-100 border-0 shadow-sm" 
                              style={{ 
                                borderRadius: '12px', 
                                overflow: 'hidden',
                                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                width: '176px' // Increased from 160px by 10%
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'translateY(-8px)';
                                e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 .125rem .25rem rgba(0,0,0,.075)';
                              }}
                            >
                              <div className="text-center p-3" 
                                style={{ 
                                  background: 'linear-gradient(to bottom, #f8f9fa, #ffffff)'
                                }}
                              >
                                {book.imageUrl ? (
                                  <img 
                                    src={book.imageUrl} 
                                    alt={book.title} 
                                    style={{
                                      height: '198px', // Increased from 180px by 10%
                                      objectFit: 'contain',
                                      filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))'
                                    }} 
                                  />
                                ) : (
                                  <div 
                                    className="d-flex justify-content-center align-items-center bg-light rounded-3"
                                    style={{ height: '198px' }} // Increased from 180px by 10%
                                  >
                                    <FaBook size={44} className="text-secondary opacity-50" /> {/* Increased from 40 by 10% */}
                                  </div>
                                )}
                              </div>
                              <Card.Body className="d-flex flex-column p-3">
                                <Card.Title className="fw-bold mb-1" style={{ fontSize: '0.9rem' }}>
                                  <div className="text-truncate">
                                    {book.title}
                                  </div>
                                </Card.Title>
                                <Card.Text className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                                  <div className="text-truncate">
                                    by {book.authors && book.authors.join(', ')}
                                  </div>
                                </Card.Text>
                                {book.categories && book.categories.length > 0 && (
                                  <div className="mb-2">
                                    <span className="badge bg-light text-secondary px-2 py-1" style={{ fontSize: '0.7rem' }}>
                                      {book.categories[0]}
                                    </span>
                                  </div>
                                )}
                                <div className="mt-auto d-grid gap-2">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    className="rounded-pill fw-semibold py-1"
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={() => handleAddToLibrary(book, 'toread')}
                                  >
                                    Add 
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="rounded-pill py-1"
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={() => window.open(book.infoLink, '_blank')}
                                  >
                                    Learn More
                                  </Button>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                  
                  {loadingSuggestions && (
                    <div className="text-center py-5 mt-4">
                      <div className="spinner-grow text-primary" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="spinner-grow text-primary mx-2" role="status" style={{ width: '1.5rem', height: '1.5rem', animationDelay: '0.2s' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="spinner-grow text-primary" role="status" style={{ width: '1.5rem', height: '1.5rem', animationDelay: '0.4s' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-muted mt-3">Finding recommendations for you...</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Container>
      </main>
    </div>
  );
}

export default NewBooksPage;